import React, { useState, useCallback } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useSpring, animated } from 'react-spring';

const PullToRefresh = ({ onRefresh, children, threshold = 80 }) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const [startY, setStartY] = useState(0);

  const handleTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    setStartY(touch.clientY);
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (isRefreshing) return;
    
    const touch = e.touches[0];
    const currentY = touch.clientY;
    const scrollTop = e.currentTarget.scrollTop;
    
    // Only trigger pull-to-refresh when at the very top (scrollTop <= 0)
    // and user is pulling down (currentY > startY)
    if (scrollTop <= 0 && currentY > startY) {
      e.preventDefault();
      const distance = Math.min(currentY - startY, threshold * 2);
      setPullDistance(distance);
      setIsPulling(distance > threshold);
    } else {
      // Reset pull state when not at top or not pulling down
      setPullDistance(0);
      setIsPulling(false);
    }
  }, [isRefreshing, startY, threshold]);

  const handleTouchEnd = useCallback(async () => {
    if (isPulling && !isRefreshing) {
      setIsRefreshing(true);
      try {
        await onRefresh();
      } finally {
        setIsRefreshing(false);
      }
    }
    // Always reset state on touch end
    setPullDistance(0);
    setIsPulling(false);
  }, [isPulling, isRefreshing, onRefresh]);

  const progressSpring = useSpring({
    transform: `scale(${Math.min(pullDistance / threshold, 1)})`,
    opacity: Math.min(pullDistance / threshold, 1),
  });

  const indicatorSpring = useSpring({
    transform: `translateY(${Math.min(pullDistance, threshold) * 0.5}px)`,
  });

  return (
    <Box
      sx={{
        height: '100%',
        overflow: 'auto',
        position: 'relative',
        touchAction: 'pan-y',
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <animated.div
        style={{
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: indicatorSpring.transform,
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <animated.div style={progressSpring}>
          <CircularProgress
            size={24}
            sx={{
              color: isPulling ? 'primary.main' : 'text.secondary',
            }}
          />
        </animated.div>
        <Typography
          variant="caption"
          sx={{
            color: isPulling ? 'primary.main' : 'text.secondary',
            fontSize: '0.75rem',
          }}
        >
          {isRefreshing ? 'Refreshing...' : isPulling ? 'Release to refresh' : 'Pull to refresh'}
        </Typography>
      </animated.div>
      
      <Box sx={{ pt: isPulling || isRefreshing ? 8 : 0 }}>
        {children}
      </Box>
    </Box>
  );
};

export default PullToRefresh;
